<script setup>
import { defineProps, ref, watch, onMounted, defineExpose, defineEmits, nextTick } from 'vue';
import { useCsrCmpState } from "@/hooks/common/useCsrCmpState";
import { isPhonePortrait } from "@/hooks/common/useUtils";
import { useDispatchGAEvent } from "@/hooks/common/useDispatchGAEvent";
import { hasRTLDirection } from "@/utils/common";

const props = defineProps({
    stateKey: {
        type: String,
        default: '',
    },
    compareList: {
        type: Array,
        default: () => [],
    },
    compareKeys: {
        type: Array,
        default: () => [],
    }
})

const emits = defineEmits(['update-compare-list']);
// eslint-disable-next-line
const { state } = useCsrCmpState({ stateKey: props.stateKey });
const { compareCategoryPath } = state.conf;
let compareHistoryData = localStorage.getItem(compareCategoryPath);
const properties = ref(null);
const compareItemList = ref([]);
const compareKeyList = ref([]);
const compareSwiperRef = ref(null);

const changeCompareList = (val) => {
    if (val.show) {
      addCompare(val);
    } else {
      delCompare(val.product.productModelNumber);
    }
    if (isPhonePortrait()) {
        initCompareSwiper();
    }
}

// 设置compare缓存
const setCompareLocalStorage = (arr) => {
  if (arr?.length) {
    localStorage.setItem(compareCategoryPath, JSON.stringify(arr));
  } else {
    localStorage.setItem(compareCategoryPath, '');
  }
};

const updateCompare = () => {
  updateCompareList();
  setCompareLocalStorage(compareItemList.value);
}

// 删除 compare 项
const delCompare = (model) => {
  if (model === "all") {
    compareItemList.value = [];
  } else {
    compareItemList.value = compareItemList.value.filter((item) => item.product.productModelNumber !== model);
  }
  updateCompare();
};

// 增加 compare 项
const addCompare = (val) => {
  compareItemList.value.push(val);
  updateCompare();
  addCompareGA(val);
};

// 添加到对比 GA
const addCompareGA = (val) => {
  const items = [];
  const { productModelNumber, productName } = val?.product || {};
  items.push({
    item_id: productModelNumber,
    item_name: productName
  });
  useDispatchGAEvent("__GA_CLICK_ADDCOMPARE_CLICK__", {
    items,
    current_url: window.location.href
  });
}

const updateCompareList = () => {
    emits('update-compare-list', compareItemList.value);
}

const initCompareSwiper = () => {
  nextTick(() => {
    // eslint-disable-next-line
    new Swiper(compareSwiperRef.value, {
      rtl: hasRTLDirection(),
      observer: true,
      observeParents: true,
      spaceBetween: 0,
      slidesPerView: "auto",
      centeredSlides: false, // 不居中显示
      initialSlide: 0, // 从第一个开始
      freeMode: true, // 自由模式，不受固定位置限制
      resistance: false, // 禁用边缘弹性效果
      slideToClickedSlide: true,
      // 限制滑动范围
      // on: {
      //   slideChangeTransitionEnd: function() {
      //     // 移动端的每一项固定了宽度，当超过470就不能滑动到第4个slider
      //     if (window.innerWidth > 470) {
      //       if (this.activeIndex > 2) { // 如果尝试滑动到第4个slide
      //         this.slideTo(2);         // 强制回到第3个slide
      //       }
      //     }
      //   }
      // }
    });
  });
};

// 跳转compare页面
const goComparePage = () => {
  const comparePath = properties?.value?.comparePath || '';
  // 路径参数
  const query = `modelNumbers=${encodeURIComponent(
    JSON.stringify(compareKeyList.value)
  )}&compareStorageKey=${compareCategoryPath}&compareNum=${compareKeyList.value.length}&plpUrl=${encodeURIComponent(
    window.location.href
  )}`;

  let connector = '?';
  if (comparePath.includes('?')) {
    connector = '&';
  }
  // eslint-disable-next-line
  location.href = comparePath + connector + query;
  const gaItems = compareItemList.value.map((item) => {
    return {
      item_id: item.product.productModelNumber,
      item_name: item.product.productName,
    };
  });
  useDispatchGAEvent("__GA_CLICK_SUBMITCOMPARE_CLICK__", {
    items: gaItems,
    current_url: window.location.href
  });
};

const init = () => {
    properties.value = state.conf;
    // 添加compare绑定值
    if (!compareHistoryData) {
        compareHistoryData = [];
    } else {
        compareHistoryData = JSON.parse(compareHistoryData);
    }
}

init();

onMounted(() => {
  if(isPhonePortrait()) {
      initCompareSwiper();
  }
});


watch(() => props.compareList, (newVal) => {
  compareItemList.value = newVal;
},
{
    immediate: true,
    deep: true,
});

watch(() => props.compareKeys, (newVal) => {
  compareKeyList.value = newVal;
},
{
    immediate: true,
});

defineExpose({
    changeCompareList,
    updateCompare,
});



</script>

<template>
    <div class="csr-product-list-v1__compare">
        <div class="csr-product-list-v1__compare-wrap">
            <div class="csr-product-list-v1__compare-header">
            <div class="title">{{ properties?.maxCompareText }} ({{ compareItemList.length }}/4)</div>
            </div>
            <div class="csr-product-list-v1__compare-content">
            <div
                ref="compareSwiperRef"
                class="csr-product-list-v1__compare-list csr-product-list-v1__compare-list_swiper"
            >
                <div class="swiper-wrapper">
                <div v-for="(_, slideIndex) in 4" class="csr-product-list-v1__compare-list-item swiper-slide" :key="slideIndex">
                    <template v-if="compareItemList[slideIndex]">
                    <div class="img-box">
                        <img
                        v-if="compareItemList[slideIndex].product.mainImage"
                        :src="compareItemList[slideIndex].product.mainImage"
                        :alt="compareItemList[slideIndex].product.altText || compareItemList[slideIndex].product.productName"
                        />
                        <img
                        v-else
                        :src="compareItemList[slideIndex].product.firstImage"
                        :alt="
                        compareItemList[slideIndex].product.firstImageAltText || compareItemList[slideIndex].product.productName
                        "
                        />
                    </div>
                    <div class="csr-product-list-v1__compare-list-item-info">
                        <div class="model">
                        {{ compareItemList[slideIndex].product.productModelNumber }}
                        </div>
                        <div class="name">
                        {{ compareItemList[slideIndex].product.productName }}
                        </div>
                    </div>
                    <i
                        class="compare-del-item-icon"
                        @click="delCompare(compareItemList[slideIndex].product.productModelNumber)"
                    />
                    </template>
                    <span v-else class="chooseTips">
                    <i class="chooseTips-text">{{ properties?.boxPlaceholderText }}</i>
                    </span>
                </div>
                </div>
            </div>
            <div class="csr-product-list-v1__compare-btns">
                <button class="cancel-btn" @click="delCompare('all')">
                {{ properties?.removeAllButtonText }}
                </button>
                <button :disabled="compareItemList.length < 2" class="go-compare-btn" @click="goComparePage">
                {{ properties?.compareButtonText }}
                </button>
            </div>
            </div>
        </div>
    </div>
</template>